import {
  AfterContentChecked,
  AfterContentInit,
  AfterViewInit,
  Component,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { GeoLocationResponse, Settings } from 'src/app/core/models';
import {
  AuthenticationService,
  EquipmentService,
  LocationService,
  ProfileService,
} from 'src/app/core/providers';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';

@Component({
  selector: 'app-location-settings',
  templateUrl: './location-settings.component.html',
  styleUrls: ['./location-settings.component.scss'],
  standalone: false,
})
export class LocationSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('zipCodeInput') zipCodeInput: any;
  @ViewChild('locationToggle') locationToggle: any;

  settingsReady = false;
  selectedGeography = 'branches';
  branches: Array<GeoLocationResponse> = [];
  public options: Settings;
  showDistance = true;
  regions: Array<GeoLocationResponse> = [];
  districts: Array<GeoLocationResponse> = [];
  allDistricts: Array<GeoLocationResponse> = [];
  isBusy = false;
  clientId: number;
  subscriptions: Subscription[] = [];
  allBranches: Array<GeoLocationResponse> = [];
  branchCount = 0;
  errorGettingLocationMsg =
    'There was a problem retrieving location data. Please enter your postal code below.';
  errorMessage: string = '';
  selectedRegionIds: Array<number> = [];
  selectedDistrictIds: Array<number> = [];

  public postalCodeIsValid = false;

  constructor(
    public profileService: ProfileService,
    public locationService: LocationService,
    public equipmentService: EquipmentService,
    public translateService: TranslateService,
    public authenticationService: AuthenticationService,
    public router: Router
  ) {}

  ngOnInit(): void {
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    this.settingsReady = true;
    this.branches = [];
    this.clientId =
      this.authenticationService.userInfoView.SelectedClient.ClientID;
    this.errorGettingLocationMsg = this.translateService.instant(
      'main.error_messages.error_locating_data'
    );
    const subscription =
      this.locationService.geoLocationResponseLoaded.subscribe((response) => {
        this.loadLocations(response);
      });
    this.subscriptions.push(subscription);

    this.settingsReady = true;
    this.options = this.profileService.getCachedSettings();

    this.showDistance = this.locationService.showDistance;
    this.loadCurrentLocation();

    if (this.options.useGeolocation) {
      this.search(true);
    } else if (this.options.zipCode && this.options.zipCode.length > 0) {
      this.onSearch(false);
    }
  }

  loadCurrentLocation = () => {
    if (!this.locationService.coordinates) {
      this.subscriptions.push(
        this.locationService.positionLoaded.subscribe((position) => {
          this.errorMessage = '';
          if (position.coords) {
            this.onSearch();
          } else {
            this.onError(new Error(this.errorGettingLocationMsg));
          }
        })
      );
      this.subscriptions.push(
        this.locationService.locationServiceErrorLoaded.subscribe(
          (response) => {
            this.onError(response);
          }
        )
      );
    }
  };

  getLabelTranslation = (value: string, replacements?: Array<string>) => {
    return this.profileService.getLabelTranslation(value, replacements);
  };

  ngOnDestroy(): void {
    this.authenticationService.startSentryTransaction(
      this.router.routerState.snapshot.url
    );
    if (!this.options.useGeolocation) {
      this.options.zipCode =
        this.zipCodeInput.nativeElement.value &&
        this.zipCodeInput.nativeElement.value.trim().length > 0
          ? this.zipCodeInput.nativeElement.value.trim()
          : null;
      if (!this.options.zipCode) {
        this.clearSelections();
      }
    }

    this.profileService.saveCachedSettings(this.options);
    this.equipmentService.clearCache();
    this.subscriptions.forEach((s: Subscription) => {
      s.unsubscribe();
    });
  }

  setSelectedGeographyTab = (value: string) => {
    this.selectedGeography = value;
  };

  getActiveClassIfSelected = (value: string) => {
    if (this.selectedGeography === value) {
      return 'segment-activated';
    }

    return '';
  };

  onUseGeolocationClick = (value: any) => {
    this.options.useGeolocation = value;
    if (value) {
      this.options.zipCode = null;
      this.zipCodeInput.nativeElement.value = null;
    }
    this.locationService.showDistance = value || this.options.zipCode !== null;
    this.clearSelections();
    this.search(value);
  };

  onSearch = (clearDistrictsAndBranches: boolean = false) => {
    this.branches = [];

    if (clearDistrictsAndBranches) {
      this.clearSelections();
    }

    this.isBusy = true;
    if (this.options.useGeolocation === true) {
      this.locationService.getLocations(this.clientId);
    } else if (this.options.zipCode !== null) {
      const updatedPostalCode =
        this.locationService.conditionallyTrimPostalCode(this.options.zipCode);
      this.locationService.getLocationsByZip(updatedPostalCode, this.clientId);
    } else {
      this.isBusy = false;
    }
  };

  loadLocations = (resp: GeoLocationResponse[]) => {
    this.allBranches = [...resp].map((x) =>
      this.mapLocationAndSetSelected(x, false)
    );

    this.regions = _.uniqBy(resp, 'ClientRegionID')
      .map(this.mapGeoLocation)
      .sort(this.regionCompare);

    this.setRegionSelections(this.regions);

    this.selectedRegionIds = this.regions
      .filter((x) => x.isSelected)
      .map((x) => x.ClientRegionID);

    this.allDistricts = _.uniqWith(
      resp,
      (loc1, loc2) =>
        loc1.ClientRegionID === loc2.ClientRegionID &&
        loc1.ClientDistrictID === loc2.ClientDistrictID
    )
      .map(this.mapGeoLocation)
      .sort(this.districtCompare);
    this.districts = [...this.allDistricts];

    this.loadDistrictsFromSelectedRegions();

    this.setDistrictSelections(this.districts);

    this.selectedDistrictIds = this.districts
      .filter((x) => x.isSelected)
      .map((x) => x.ClientDistrictID);

    if (this.selectedRegionIds.length || this.selectedDistrictIds.length) {
      this.loadBranchesFromDistrictSelections();
      // this.branches = [...resp].filter(
      //         x => this.options.selectedRegions ? this.options.selectedRegions.indexOf(x.ClientRegionID) > -1 : false
      //     )
      //     .map(x => this.mapLocationAndSetSelected(x, false));
    } else {
      this.branches = [...resp].map((x) =>
        this.mapLocationAndSetSelected(x, false)
      );
    }
    // this.allBranches = [...this.branches];
    this.setBranchSelections(this.branches);
    this.isBusy = false;
    this.authenticationService.finishSentryTransaction();
  };

  setRegionSelections = (regions: Array<GeoLocationResponse>) => {
    if (
      regions &&
      regions.length &&
      this.options !== undefined &&
      this.options.selectedRegions &&
      this.options.selectedRegions.length
    ) {
      regions
        .filter((x) =>
          this.options.selectedRegions.length
            ? this.options.selectedRegions.indexOf(x.ClientRegionID) > -1
            : false
        )
        .map((x) => (x.isSelected = true));
    }
  };

  setDistrictSelections = (districts: Array<GeoLocationResponse>) => {
    if (
      districts &&
      districts.length &&
      this.options !== undefined &&
      this.options.selectedDistricts &&
      this.options.selectedDistricts.length
    ) {
      districts
        .filter((x) =>
          this.options.selectedDistricts.length
            ? this.options.selectedDistricts.indexOf(x.ClientDistrictID) > -1
            : false
        )
        .map((x) => (x.isSelected = true));
    }
  };

  setBranchSelections = (locations: Array<GeoLocationResponse>) => {
    if (
      locations &&
      locations.length &&
      this.options.selectedBranches &&
      this.options.selectedBranches.length
    ) {
      locations
        .filter((x) =>
          this.options.selectedBranches
            ? this.options.selectedBranches.indexOf(x.BranchID) > -1
            : false
        )
        .map((x) => (x.isSelected = true));
    }
  };

  clearSelections = () => {
    this.regions = [];
    this.districts = [];
    this.branches = [];
    this.options.selectedBranches = [];
    this.options.selectedDistricts = [];
    this.options.selectedRegions = [];
  };

  compare = (a: GeoLocationResponse, b: GeoLocationResponse) => {
    const v1 = a.Region.toLowerCase();
    const v2 = b.Region.toLowerCase();

    let comparison = 0;
    if (v1 > v2) {
      comparison = 1;
    } else if (v1 < v2) {
      comparison = -1;
    }
    return comparison;
  };

  getBranchCount = (): string => {
    let cnt = this.branches.filter((x) => x.isSelected).length;
    if (cnt === 0) {
      cnt = this.branches.length;
    }
    return cnt === 0 ? '' : cnt.toString();
  };

  getRegionCount = (): string => {
    const selectedRegionCount = this.regions.filter((x) => x.isSelected).length;
    return selectedRegionCount === 0 ? '' : selectedRegionCount.toString();
  };

  getDistrictCount = (): string => {
    let selectedDistrictCount = this.districts.filter(
      (x) => x.isSelected
    ).length;
    if (selectedDistrictCount === 0) {
      selectedDistrictCount = this.districts.length;
    }
    return selectedDistrictCount === 0 ? '' : selectedDistrictCount.toString();
  };

  checkIfHidden = (value: string) => {
    if (value === 'regions' && this.getRegionCount().length === 0) {
      return 'hidden-hide-when';
    }
    if (value === 'branches' && this.getBranchCount().length === 0) {
      return 'hidden-hide-when';
    }
    if (value === 'districts' && this.getDistrictCount().length === 0) {
      return 'hidden-hide-when';
    }

    if (this.selectedGeography === value) {
      return 'count-badge-active';
    }

    return 'count-badge';
  };

  convertToImperial = (location: GeoLocationResponse): string => {
    const distance = location.Distance || 0;
    if (
      (!this.options.useGeolocation && !this.postalCodeIsValid) ||
      distance.toString().indexOf('e+') !== -1
    ) {
      return this.translateService.instant('main.labels.na');
    }

    if (distance === 0) {
      return '0mi';
    } else if (distance < 1) {
      return (distance * 3.28084).toFixed(0) + 'ft';
    } else {
      return (distance * 0.000621371).toFixed(1) + 'mi';
    }
  };

  validatePostalCode = (event: any) => {
    const zipCode = this.zipCodeInput
      ? this.zipCodeInput.nativeElement.value
      : null;

    const isValid = this.locationService.validatePostalCode(zipCode);

    if (isValid) {
      this.options.zipCode = zipCode;
      this.locationService.showDistance = true;
    } else {
      this.locationService.showDistance = this.options.useGeolocation;
    }

    this.postalCodeIsValid = isValid;
  };

  public search = (useGeolocation: boolean) => {
    this.branches = [];

    if (useGeolocation === true) {
      if (this.locationService.coordinates) {
        this.onSearch();
      } else {
        this.getCurrentPosition();
      }
    }
  };

  public getCurrentPosition = () => {
    this.isBusy = true;
    this.locationService.getCurrentPosition();
  };

  public onError = (error: Error) => {
    Sentry.captureException(error);
    this.locationToggle.isChecked = false;
    this.options.useGeolocation = false;
    this.errorMessage = this.errorGettingLocationMsg;
    this.isBusy = false;
  };

  sortByBranch = (a: GeoLocationResponse, b: GeoLocationResponse) => {
    const v1 = a.Branch.toLowerCase();
    const v2 = b.Branch.toLowerCase();

    let comparison = 0;
    if (v1 > v2) {
      comparison = 1;
    } else if (v1 < v2) {
      comparison = -1;
    }
    return comparison;
  };

  getFormattedLocation = (location: GeoLocationResponse) => {
    let formattedLocation = location.BranchCity ? location.BranchCity : '';
    formattedLocation += formattedLocation.length > 0 ? ', ' : '';
    formattedLocation += location.BranchState ? location.BranchState : '';
    return formattedLocation;
  };

  loadDistrictsFromSelectedRegions = () => {
    const filtered = this.selectedRegionIds.length
      ? [...this.allDistricts].filter(
          (x) => this.selectedRegionIds.indexOf(x.ClientRegionID) > -1
        )
      : [...this.allDistricts];
    this.districts = _.uniqBy(filtered, 'ClientDistrictID');
    this.districts.forEach((r) => (r.isSelected = false));
  };

  onRegionChanged = (region: GeoLocationResponse) => {
    region.isSelected = !region.isSelected;

    this.selectedRegionIds = this.regions
      .filter((x) => x.isSelected)
      .map((x) => x.ClientRegionID);
    this.selectedDistrictIds = [];

    this.options.selectedRegions = this.selectedRegionIds.slice();

    this.options.selectedBranches = [];
    this.profileService.updateSelectedBranches([]);
    this.loadDistrictsFromSelectedRegions();
    this.loadBranchesFromDistrictSelections();

    this.options.selectedDistricts = [];
  };

  onLocationClick = (location: GeoLocationResponse) => {
    location.isSelected = !location.isSelected;

    const selectedBranches = this.branches
      .filter((x) => x.isSelected)
      .map((x) => x.BranchID);

    this.options.selectedBranches = selectedBranches.slice();
  };

  showBranch = (element: any, index: any, array: any) => {
    if (this.selectedDistrictIds.length) {
      return (
        this.selectedDistrictIds.indexOf(element.ClientDistrictID) > -1 &&
        (!this.selectedRegionIds.length ||
          this.selectedRegionIds.indexOf(element.ClientRegionID) > -1)
      );
    } else if (this.selectedRegionIds.length) {
      return (
        !this.selectedRegionIds.length ||
        this.selectedRegionIds.indexOf(element.ClientRegionID) > -1
      );
    }
    return true;
  };

  loadBranchesFromDistrictSelections = () => {
    if (this.showDistance) {
      this.branches = [...this.allBranches]
        .filter(this.showBranch)
        .map((x) => this.mapLocationAndSetSelected(x, false))
        .sort((a, b) => a.Distance - b.Distance);
    } else {
      this.branches = [...this.allBranches]
        .filter(this.showBranch)
        .map((x) => this.mapLocationAndSetSelected(x, false))
        .sort(this.sortByBranch);
    }

    // When districts/regions are active, remove any saved branch selections that are
    // no longer in the filtered list — stale IDs would cause contradicting SQL filters.
    if (this.selectedDistrictIds.length || this.selectedRegionIds.length) {
      const visibleBranchIds = new Set(this.branches.map((b) => b.BranchID));
      const validSelected = this.options.selectedBranches.filter((id) =>
        visibleBranchIds.has(id)
      );
      if (validSelected.length !== this.options.selectedBranches.length) {
        this.options.selectedBranches = validSelected;
        this.profileService.updateSelectedBranches(validSelected);
      }
    }
  };

  onDistrictChanged = (district: GeoLocationResponse) => {
    district.isSelected = !district.isSelected;

    this.selectedDistrictIds = this.districts
      .filter((x) => x.isSelected)
      .map((x) => x.ClientDistrictID);

    this.options.selectedDistricts = this.selectedDistrictIds.slice();

    //this.branches.forEach(r => r.isSelected = false);
    this.options.selectedBranches = [];
    this.profileService.updateSelectedBranches([]);

    this.loadBranchesFromDistrictSelections();

    this.options.selectedDistricts = this.selectedDistrictIds;
  };

  public mapGeoLocation = (
    geoLocation: GeoLocationResponse
  ): GeoLocationResponse => {
    const region = new GeoLocationResponse({
      ...geoLocation,
      isSelected: false,
    });

    return region;
  };

  public mapLocationAndSetSelected = (
    geoLocation: GeoLocationResponse,
    isSelected: boolean
  ): GeoLocationResponse => {
    const loc = new GeoLocationResponse({
      ...geoLocation,
      isSelected: isSelected,
    });

    return loc;
  };

  public regionCompare = (
    locA: GeoLocationResponse,
    locB: GeoLocationResponse
  ): number => {
    if (locA.Region < locB.Region) {
      return -1;
    }

    if (locA.Region > locB.Region) {
      return 1;
    }

    return 0;
  };

  public districtCompare = (
    locA: GeoLocationResponse,
    locB: GeoLocationResponse
  ): number => {
    if (locA.District < locB.District) {
      return -1;
    }

    if (locA.District > locB.District) {
      return 1;
    }

    return 0;
  };
}
